/** Ordering of the media library. Pure, so it is unit-tested without a DOM. */
import type { MediaAsset, MediaFolder } from '@ve/core';
import type { LibrarySort, LibrarySortKey } from '../../state/ui-store.ts';

export const SORT_LABELS: Record<LibrarySortKey, string> = {
  name: 'Name',
  captured: 'Date taken',
  imported: 'Date added',
  duration: 'Duration',
  kind: 'Type',
  size: 'File size',
};

export const SORT_DESCRIPTIONS: Record<LibrarySortKey, string> = {
  name: 'Alphabetical; numbers in names sort naturally (2 before 10)',
  captured:
    'When the photo or video was recorded (EXIF / camera metadata), else the file’s modification date',
  imported: 'The order in which media was added to the project',
  duration: 'Clip length; stills come last',
  kind: 'Videos, then photos, then audio',
  size: 'Size of the file on disk',
};

/**
 * When the media was recorded, in ms since the epoch: the capture date from the file's metadata,
 * else the file's modification time, else when it was imported.
 */
export function captureTime(asset: MediaAsset): number {
  if (asset.capturedAt) {
    const t = Date.parse(asset.capturedAt);
    if (Number.isFinite(t)) return t;
  }
  return asset.location.lastModified ?? Date.parse(asset.importedAt);
}

const KIND_RANK = { video: 0, image: 1, audio: 2 } as const;

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

/** The sort value of an asset; `undefined` always sorts last, whatever the direction. */
function sortValue(asset: MediaAsset, key: LibrarySortKey, importIndex: number): number | string | undefined {
  switch (key) {
    case 'name':
      return asset.name;
    case 'captured':
      return captureTime(asset);
    case 'imported':
      return importIndex;
    case 'duration':
      return asset.duration;
    case 'kind':
      return KIND_RANK[asset.kind];
    case 'size':
      return asset.location.size;
  }
}

function compareValues(a: number | string, b: number | string): number {
  return typeof a === 'string' && typeof b === 'string'
    ? collator.compare(a, b)
    : (a as number) - (b as number);
}

/**
 * Sorts assets for display. `mediaOrder` is the project's import order, used for "Date added" and
 * as the final tie-break, so equal items keep a stable order.
 */
export function sortMedia(
  assets: MediaAsset[],
  mediaOrder: readonly string[],
  sort: LibrarySort,
): MediaAsset[] {
  const index = new Map(mediaOrder.map((id, i) => [id, i]));
  const rows = assets.map((asset) => {
    const importIndex = index.get(asset.id) ?? Number.MAX_SAFE_INTEGER;
    return { asset, importIndex, value: sortValue(asset, sort.key, importIndex) };
  });
  const sign = sort.descending ? -1 : 1;
  rows.sort((a, b) => {
    if (a.value === undefined || b.value === undefined) {
      if (a.value !== b.value) return a.value === undefined ? 1 : -1;
    } else {
      const primary = compareValues(a.value, b.value);
      if (primary !== 0) return sign * primary;
    }
    return collator.compare(a.asset.name, b.asset.name) || a.importIndex - b.importIndex;
  });
  return rows.map((r) => r.asset);
}

/** Folders are always listed by name, before media (like a file browser). */
export function sortFolders(folders: MediaFolder[]): MediaFolder[] {
  return [...folders].sort((a, b) => collator.compare(a.name, b.name));
}
